/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import * as applicationStatuses from "../rentalContract/applicationStatus";
import formSteps from "./formSteps";
import formStepsFlexService from "./flexService/formSteps";
import ReactGA from "react-ga4";
import {
  fromBoolean,
  fromDate,
  fromFloat,
  fromIban,
  fromInsurance,
  fromUserInputtedFloat,
  getConvertedFormDataBase,
  persistAdditionalClients,
  pickAndConvert,
  prepareFormDataBase,
  sanitizeAddressData,
  sanitizeOrganizationData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toFloat,
  toIban,
  toInsurance,
  toUserInputtedFloat,
} from "../formServiceFunctions";
import _includes from "lodash/includes";
import { Moment } from "moment";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { Customer } from "../../types/Entities/Customer";
import _has from "lodash/has";
import { getProductData } from "../productService";
import _isEmpty from "lodash/isEmpty";
import ApiClient from "../apiClient";
import _merge from "lodash/merge";
import { FormServiceBase, FormServiceSaveProps } from "../../types/FormServiceBase";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTY_TYPE]: {
      product: ["contractPartyType", "reasonForDispute"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]: {
      product: [
        "caseCategory",
        "affectedPartyType",
        "contractDate",
        "subjectOfDispute",
        "claimedAmountClient",
        "clientHasAlreadyClaimed",
        "claimedDateClient",
        "claimedDeadlineClient",
        "defenseProcessStatus",
        "paymentOrderDate",
        "enforcementOrderDate",
        "statementOfClaimFiledDate",
        "contractualPartnerHasLawyer",
      ],
      opponentLawFirm: ["name", "email", "telephone"],
      "opponentLawFirm.address": ["streetAddress", "postalCode", "addressLocality"],
      customer: ["telephone"],
      "customer.person": ["gender", "givenName", "familyName"],
      "customer.person.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      client: ["gender", "givenName", "familyName"],
      "client.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
      clientBankData: ["iban"],
      additionalClients: [],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DETAILS]: {
      product: [
        "reasonForDispute",
        "housingDeficienciesDescriptionClient",
        "previousBasicRent",
        "previousRentIncludingHeating",
        "futureBasicRent",
        "futureRentIncludingHeating",
        "depositAmount",
        "cancellationReceivedDate",
        "rentalContractStartDate",
        "rentalContractEndDate",
      ],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTNER]: {
      product: ["opposingPartyType", "propertyManagerReference", "hasPropertyManager"],
      opposingPartyLegalEntity: ["legalName", "email", "telephone"],
      "opposingPartyLegalEntity.address": [
        "streetAddress",
        "postalCode",
        "addressLocality",
        "postOfficeBoxNumber",
        "postOfficeBoxPostalCode",
        "postOfficeBoxAddressLocality",
      ],
      opposingPartyPerson: ["gender", "givenName", "familyName", "email", "telephone"],
      "opposingPartyPerson.residenceAddress": [
        "streetAddress",
        "postalCode",
        "addressLocality",
        "postOfficeBoxNumber",
        "postOfficeBoxPostalCode",
        "postOfficeBoxAddressLocality",
      ],
      propertyManager: ["legalName", "email", "telephone", "fax"],
      "propertyManager.address": ["streetAddress", "postalCode", "addressLocality"],
      propertyAddress: ["streetAddress", "postalCode", "addressLocality", "addressAddition"],
    },
    [applicationStatuses.APPLICATION_STATUS_DEBT_COLLECTION]: {
      product: ["debtCollectionAgencyReference"],
      debtCollectionAgency: ["name", "email", "telephone"],
      "debtCollectionAgency.address": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]: {
      product: ["personalTextMessage"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]: {
      product: ["clientHasNoContractDocument"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_INSURANCE]: {
      insurance: ["insurance", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType", "deductible"],
      "insurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      product: ["powerOfAttorney"],
    },
  });

  //todo migrate functions into formServiceFunctions as soon as it supports typescript
  fromToDateConversionFields = {
    convertFrom: (value: string) => fromDate(value),
    convertTo: (value: Moment) => toDate(value),
  };

  fromToBooleanConversionFields = {
    convertFrom: (value: boolean | null) => fromBoolean(value),
    convertTo: (value: string | null) => toBoolean(value),
  };

  fromToFloatConversionFields = {
    convertFrom: (value: number | null) => fromFloat(value),
    convertTo: (value: string | null) => toFloat(value),
  };

  fromToIbanConversionFields = {
    convertFrom: (value: string | null) => fromIban(value),
    convertTo: (value: string | null) => toIban(value),
  };

  conversionFields = [
    {
      path: "contractDate",
      ...this.fromToDateConversionFields,
    },
    {
      path: "claimedDateClient",
      ...this.fromToDateConversionFields,
    },
    {
      path: "claimedDeadlineClient",
      ...this.fromToDateConversionFields,
    },
    {
      path: "paymentOrderDate",
      ...this.fromToDateConversionFields,
    },
    {
      path: "enforcementOrderDate",
      ...this.fromToDateConversionFields,
    },
    {
      path: "statementOfClaimFiledDate",
      ...this.fromToDateConversionFields,
    },
    {
      path: "legalExpenseInsurance",
      ...this.fromToBooleanConversionFields,
    },
    {
      path: "contractualPartnerHasLawyer",
      ...this.fromToBooleanConversionFields,
    },
    {
      path: "clientHasAlreadyClaimed",
      ...this.fromToBooleanConversionFields,
    },
    {
      path: "hasPropertyManager",
      ...this.fromToBooleanConversionFields,
    },
    {
      path: "insurance.deductible",
      ...this.fromToFloatConversionFields,
    },
    {
      path: "previousBasicRent",
      ...this.fromToFloatConversionFields,
    },
    {
      path: "previousRentIncludingHeating",
      ...this.fromToFloatConversionFields,
    },
    {
      path: "futureBasicRent",
      ...this.fromToFloatConversionFields,
    },
    {
      path: "futureRentIncludingHeating",
      ...this.fromToFloatConversionFields,
    },
    {
      path: "depositAmount",
      ...this.fromToFloatConversionFields,
    },
    {
      path: "cancellationReceivedDate",
      ...this.fromToDateConversionFields,
    },
    {
      path: "rentalContractStartDate",
      ...this.fromToDateConversionFields,
    },
    {
      path: "rentalContractEndDate",
      ...this.fromToDateConversionFields,
    },
    {
      path: "claimedAmountClient",
      convertFrom: (value: string | null) => fromUserInputtedFloat(value),
      convertTo: (value: string | null) => toUserInputtedFloat(value),
    },
    {
      path: "clientBankData.iban",
      ...this.fromToIbanConversionFields,
    },
    {
      path: "insurance.insurance",
      convertFrom: fromInsurance,
      convertTo: toInsurance,
    },
  ];

  statusFunctions = {
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: () => {
      ReactGA.event({
        category: "Power of Attorney",
        action: "Contract PoA granted",
      });
    },
  };

  save = async ({
    formData,
    applicationStatus,
    nextApplicationStatus,
    product,
    customer,
    updateMutation,
    createMutation,
    refreshCustomer,
    deleteMutation,
  }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields()[applicationStatus] || {};

    let hasCustomerUpdates = false;

    if (_has(fields, "customer.person.residenceAddress")) {
      hasCustomerUpdates = true;
      let addressData = pickAndConvert(formData.customer.person.residenceAddress, fields["customer.person.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: customer.person!.residenceAddress!.id!,
        data: addressData,
      });
    }

    if (_has(fields, "customer.person") && !!customer.person?.id) {
      hasCustomerUpdates = true;
      const convertedFormData = this.getConvertedFormData(formData);
      let customerPersonData = pickAndConvert(convertedFormData.customer.person, fields["customer.person"]);
      customerPersonData = sanitizePersonData(customerPersonData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: customer.person.id,
        data: customerPersonData,
      });
    }

    if (_has(fields, "customer")) {
      hasCustomerUpdates = true;
      const customerData = pickAndConvert(formData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "client.residenceAddress")) {
      const baseAddressData = _includes(["customer", "unknown"], formData.affectedPartyType)
        ? formData.customer.person.residenceAddress
        : formData.client.residenceAddress;
      let addressData = pickAndConvert(baseAddressData, fields["client.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.client.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "client")) {
      const convertedFormData = this.getConvertedFormData(formData);
      const baseClientData = convertedFormData.affectedPartyType === "customer" ? convertedFormData.customer.person : convertedFormData.client;
      let clientData = pickAndConvert(baseClientData, fields.client);
      clientData = sanitizePersonData(clientData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.client.id,
        data: clientData,
      });
    }

    if (_has(fields, "opponentLawFirm")) {
      let opponentLawFirmData = pickAndConvert(formData.opponentLawFirm, fields["opponentLawFirm"]);
      opponentLawFirmData = sanitizeOrganizationData(opponentLawFirmData);
      opponentLawFirmData.name = opponentLawFirmData.name || (formData.opponentLawyer.familyName ? `Kanzlei ${formData.opponentLawyer.familyName}` : "");
      await updateMutation.mutateAsync({
        uri: "/organizations",
        id: formData.opponentLawFirm.id,
        data: opponentLawFirmData,
      });
    }

    if (_has(fields, "debtCollectionAgency")) {
      let debtCollectionAgencyData = pickAndConvert(formData.debtCollectionAgency, fields["debtCollectionAgency"]);
      debtCollectionAgencyData = sanitizeOrganizationData(debtCollectionAgencyData);
      if (product.debtCollectionAgency === null) {
        //@ts-ignore
        product = await updateMutation.mutateAsync({
          uri: getProductData(product.name, "apiUri"),
          id: product.id,
          data: {
            debtCollectionAgency: "/organizations/new",
          },
        });
      }
      await updateMutation.mutateAsync({
        uri: "/organizations",
        id: product.debtCollectionAgency.id,
        data: debtCollectionAgencyData,
      });
      if (_has(fields, "debtCollectionAgency.address")) {
        let debtCollectionAgencyAddressData = pickAndConvert(formData.debtCollectionAgency.address, fields["debtCollectionAgency.address"]);
        debtCollectionAgencyAddressData = sanitizeAddressData(debtCollectionAgencyAddressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.debtCollectionAgency.address.id,
          data: debtCollectionAgencyAddressData,
        });
      }
    }

    if (_has(fields, "opponentLawFirm.address")) {
      let opponentLawFirmAddressData = pickAndConvert(formData.opponentLawFirm.address, fields["opponentLawFirm.address"]);
      opponentLawFirmAddressData = sanitizeAddressData(opponentLawFirmAddressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: formData.opponentLawFirm.address.id,
        data: opponentLawFirmAddressData,
      });
    }

    if (_has(fields, "clientBankData")) {
      const clientBankData = pickAndConvert(formData.clientBankData, fields["clientBankData"]);
      if (clientBankData.iban) {
        clientBankData.iban = toIban(clientBankData.iban);
      } else {
        delete clientBankData.iban;
      }
      if (!_isEmpty(clientBankData)) {
        await updateMutation.mutateAsync({
          uri: "/bank_datas",
          id: formData.clientBankData.id,
          data: clientBankData,
        });
      }
    }

    if (formData.opposingPartyType === "opposingPartyLegalEntity") {
      if (_has(fields, "opposingPartyLegalEntity")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyLegalEntity, fields.opposingPartyLegalEntity);
        opposingPartyData = sanitizeOrganizationData(opposingPartyData);
        await updateMutation.mutateAsync({
          uri: "/organizations",
          id: product.opposingPartyLegalEntity.id,
          data: opposingPartyData,
        });
      }
      if (_has(fields, "opposingPartyLegalEntity.address")) {
        let addressData = pickAndConvert(formData.opposingPartyLegalEntity.address, fields["opposingPartyLegalEntity.address"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyLegalEntity.address.id,
          data: addressData,
        });
      }
    }

    if (formData.opposingPartyType === "opposingPartyPerson") {
      if (_has(fields, "opposingPartyPerson")) {
        let opposingPartyData = pickAndConvert(formData.opposingPartyPerson, fields.opposingPartyPerson);
        opposingPartyData = sanitizePersonData(opposingPartyData);
        await updateMutation.mutateAsync({
          uri: "/people",
          id: product.opposingPartyPerson.id,
          data: opposingPartyData,
        });
      }
      if (_has(fields, "opposingPartyPerson.residenceAddress")) {
        let addressData = pickAndConvert(formData.opposingPartyPerson.residenceAddress, fields["opposingPartyPerson.residenceAddress"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.opposingPartyPerson.residenceAddress.id,
          data: addressData,
        });
      }
    }

    if (formData.hasPropertyManager === "yes") {
      if (_has(fields, "propertyManager")) {
        let propertyManagerData = pickAndConvert(formData.propertyManager, fields.propertyManager);
        propertyManagerData = sanitizeOrganizationData(propertyManagerData);
        await updateMutation.mutateAsync({
          uri: "/organizations",
          id: product.propertyManager.id,
          data: propertyManagerData,
        });
      }
      if (_has(fields, "propertyManager.address")) {
        let addressData = pickAndConvert(formData.propertyManager.address, fields["propertyManager.address"]);
        addressData = sanitizeAddressData(addressData);
        await updateMutation.mutateAsync({
          uri: "/addresses",
          id: product.propertyManager.address.id,
          data: addressData,
        });
      }
    }

    if (_has(fields, "insurance")) {
      const convertedFormData = this.getConvertedFormData(formData);
      let insuranceData = pickAndConvert(convertedFormData.insurance, fields["insurance"]);
      await ApiClient.put("/customer_insurances/" + product.insurance.id, { body: JSON.stringify(insuranceData) });
    }

    if (_has(fields, "insurance.policyHolder")) {
      const basePolicyHolderData = formData.insurance.policyHolderType === "me" ? formData.customer.person : formData.insurance.policyHolder;
      let policyHolderData = pickAndConvert(basePolicyHolderData, fields["insurance.policyHolder"]);
      policyHolderData = sanitizePersonData(policyHolderData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.insurance.policyHolder.id,
        data: policyHolderData,
      });
    }

    if (_has(fields, "additionalClients")) {
      await persistAdditionalClients(formData, product, updateMutation, createMutation, deleteMutation);
    }

    if (_has(fields, "propertyAddress")) {
      let addressData = pickAndConvert(formData.propertyAddress, fields.propertyAddress);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.propertyAddress.id,
        data: addressData,
      });
    }

    let productData = {};
    if (nextApplicationStatus) {
      (productData as { applicationStatus: number }).applicationStatus = nextApplicationStatus;
      // @ts-ignore
      this.statusFunctions[applicationStatus] && this.statusFunctions[applicationStatus]();
    }

    if (fields && fields.product) {
      productData = _merge(productData, pickAndConvert(formData, fields.product));
      productData = this.getConvertedFormData(productData);
    }
    if (hasCustomerUpdates) {
      await refreshCustomer();
    }
    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: productData,
    });
  };

  getConvertedFormData = (data: Record<string, any>) => {
    return getConvertedFormDataBase(data, this.conversionFields);
  };

  getFrontendOnlyFieldsInitialValues = (product: AbstractCase) => ({
    howManyClients: product.additionalClients!.length > 1 ? "threeOrMoreClients" : product.additionalClients!.length === 1 ? "twoClients" : "oneClient",
    numberOfAdditionalClients: product.additionalClients?.length ? String(product.additionalClients.length) : "",
    contractAddressIsCurrentAddress: "",
  });

  prepareFormData = (product: AbstractCase, customer: Customer) => {
    const formData = prepareFormDataBase(product, customer, this.conversionFields);
    return { ...this.getFrontendOnlyFieldsInitialValues(product), ...formData };
  };

  getAvailableSteps = (formData: Record<string, any>, product: AbstractCase) => {
    let availableSteps = [];

    if (product.insurance.specialTermsTag === "advocardFlex") {
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]);
      availableSteps.push(formStepsFlexService[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);
      return availableSteps;
    }

    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTY_TYPE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]);
    if (["enforceClaim", "defendClaim"].includes(formData.caseCategory)) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DETAILS]);
    }
    if (["enforceClaim", "defendClaim"].includes(formData.caseCategory)) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_PARTNER]);
    }
    if (formData.caseCategory === "defendClaim" && formData.defenseProcessStatus === "debtCollection") {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DEBT_COLLECTION]);
    }
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DESCRIPTION]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DOCUMENTS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_INSURANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
    //for dependencies that are changed in the same step as we are we need to check product instead of formdata to not
    // invalidate our next step since formdata updates before the actual request is fired
    if (_includes(["enforceClaim", "defendClaim"], formData.caseCategory) && !product.powerOfAttorney) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
    }
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);

    return availableSteps;
  };
}

export default new FormService();
